/**
 * Waitlist entries + the invite action — BETA-ONLY admin tooling.
 *
 * DELETE THIS FILE when beta mode ends, together with
 * `src/app/(admin)/waitlist.tsx`, `use-waitlist-stats.ts` and the
 * `waitlist-invite` edge function. Every caller is behind `isBetaMode()`, so
 * removal is a clean operation with no side effects. Sibling of
 * `use-waitlist-stats.ts`, which supplies the aggregate counters.
 *
 * WHY THIS EXISTS: the beta is invite-only — `AuthProvider.signUp` refuses any
 * email whose waitlist status is not `invited` — and until now nothing in the
 * product could set that status. The dashboard showed an "Invited" counter that
 * nothing could increment. Reads come straight from the table under the existing
 * `admin_read_waitlist` RLS policy; the WRITE goes through the edge function,
 * because there is no admin UPDATE policy and the invitation also sends mail.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';

export type WaitlistRole = 'startup' | 'investor' | 'undecided';
export type WaitlistStatus = 'waiting' | 'invited' | 'converted';

export interface WaitlistEntry {
  id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  title: string | null;
  linkedin_url: string | null;
  role: WaitlistRole;
  status: WaitlistStatus;
  verified: boolean;
  position: number;
  referral_count: number;
  created_at: string;
  invited_at: string | null;
}

export const waitlistKeys = {
  entries: () => ['admin', 'waitlist', 'entries'] as const,
};

/** A handful of rows so the screen is explorable in mock mode. */
const MOCK_ENTRIES: WaitlistEntry[] = [
  {
    id: 'mock-1', email: 'founder@northwind.io', full_name: 'Amara Diallo',
    company_name: 'Northwind', title: 'CEO', linkedin_url: null,
    role: 'startup', status: 'waiting', verified: true, position: 12,
    referral_count: 3, created_at: '2026-07-02T10:00:00Z', invited_at: null,
  },
  {
    id: 'mock-2', email: 'partner@meridian.vc', full_name: 'Tom Bergström',
    company_name: 'Meridian Ventures', title: 'Partner', linkedin_url: null,
    role: 'investor', status: 'invited', verified: true, position: 4,
    referral_count: 0, created_at: '2026-06-28T09:00:00Z', invited_at: '2026-07-30T09:00:00Z',
  },
  {
    id: 'mock-3', email: 'sam@unsure.dev', full_name: null,
    company_name: null, title: null, linkedin_url: null,
    role: 'undecided', status: 'waiting', verified: false, position: 31,
    referral_count: 0, created_at: '2026-07-19T14:00:00Z', invited_at: null,
  },
];

/**
 * Every waitlist entry, newest signup last so position order reads naturally.
 * Relies on the `admin_read_waitlist` policy — a non-admin gets an empty list
 * rather than an error, which is the correct RLS behaviour, not a bug.
 */
export function useWaitlistEntries() {
  return useQuery({
    queryKey: waitlistKeys.entries(),
    queryFn: async (): Promise<WaitlistEntry[]> => {
      if (mode.showSampleData) return MOCK_ENTRIES;
      if (!isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('waitlist')
        .select(
          'id, email, full_name, company_name, title, linkedin_url, role, status, verified, position, referral_count, created_at, invited_at',
        )
        .order('position', { ascending: true });

      if (error) throw error;
      return (data ?? []) as unknown as WaitlistEntry[];
    },
    staleTime: 60 * 1000,
  });
}

export interface InviteResult {
  email: string;
  role: WaitlistRole | null;
  status: 'invited';
  invitedAt: string | null;
  /** True when the row was already `invited` and this was a re-send. */
  alreadyInvited: boolean;
  /** The invitation stands even when this is false — see the edge function. */
  emailSent: boolean;
  emailError?: string;
}

/**
 * Invite one person off the waitlist.
 *
 * Takes the row id rather than the email so the admin acts on the row in front
 * of them instead of a re-typed address — inviting the wrong person is not
 * something you can take back.
 */
export function useInviteFromWaitlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (waitlistId: string): Promise<InviteResult> => {
      const { data, error } = await supabase.functions.invoke('waitlist-invite', {
        body: { waitlistId },
      });
      if (error) throw error;
      return data as InviteResult;
    },
    onSuccess: () => {
      // The list AND the dashboard counters both move.
      queryClient.invalidateQueries({ queryKey: waitlistKeys.entries() });
      queryClient.invalidateQueries({ queryKey: ['admin-waitlist-stats'] });
    },
  });
}
